"use client"
import { TEAMS_LINK, TeamsLogo, WhatsAppLogo, WHATSAPP_NUMBER } from "@/lib";
import { Menu, X, Sparkles, ChevronRight } from "lucide-react";
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

// Utility for cleaner tailwind classes
function cn(...inputs: (string | undefined | null | false)[]) {
  return twMerge(clsx(inputs));
}

export default function Header() {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [activeSection, setActiveSection] = useState('');

    const navLinks = [
        { name: 'About', href: '#about' },
        { name: 'Services', href: '#services' },
        // Feature link with special property
        { name: 'AI Architect', href: '#analyzer', isFeature: true }, 
        { name: 'AI R&D', href: '#ai' },
        { name: 'IT Consultant', href: '#plans' },
        {name:'Cost Estimator',href:'#calculator'},
        {name:'Web Solution',href:'#web-solution'},
        { name: 'Products', href: '#products' },
    ];

    // Handle Scroll Effects (Glassmorphism & Active Spy)
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);

            // specific logic to find which section is currently in view
            const sections = navLinks.map(link => link.href.substring(1));
            let current = '';
            for (const section of sections) {
                const element = document.getElementById(section);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    if (rect.top <= 150 && rect.bottom >= 150) {
                        current = section;
                        break;
                    }
                }
            }
            setActiveSection(current);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToSection = (e: React.MouseEvent, href: string) => {
        e.preventDefault();
        const sectionId = href.replace('#', '');
        const section = document.getElementById(sectionId);
        
        if (section) {
            // HeaderOffset updated to 100px for better clearance below the fixed bar
            const headerOffset = 100; 
            const elementPosition = section.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
            
            setActiveSection(sectionId);

            // FIX: If the mobile menu is open, delay closing it to prevent scroll interruption.
            if (isMobileMenuOpen) {
                // Use a standard delay for smooth scroll completion.
                setTimeout(() => {
                    setIsMobileMenuOpen(false);
                }, 600); // Increased delay slightly to 600ms for robustness
            } 
        }
    };

    return (
        <motion.header 
            id="header"
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5 }}
            className={cn(
                "fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b",
                scrolled 
                    ? "bg-slate-950/80 backdrop-blur-xl border-slate-800/50 shadow-lg shadow-black/20 py-3" 
                    : "bg-transparent border-transparent py-5"
            )}
        >
            <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center">
                    
                    {/* Logo */}
                    <div className="flex-shrink-0 relative z-50">
                        <a 
                            href="#hero" 
                            onClick={(e) => scrollToSection(e, '#hero')} 
                            className="group flex items-center gap-2"
                        >
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-600 to-cyan-500 flex items-center justify-center text-white font-bold text-lg shadow-lg group-hover:shadow-indigo-500/50 transition-all">
                                M
                            </div>
                            <span className="text-xl font-bold text-white tracking-tight group-hover:text-indigo-200 transition-colors">
                                WorkSpace
                            </span>
                        </a>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden lg:flex items-center gap-1">
                        <div className="flex items-center bg-slate-900/50 rounded-full px-2 py-1.5 border border-slate-800/50 backdrop-blur-md mr-4">
                            {navLinks.map((link) => {
                                const isActive = activeSection === link.href.substring(1);
                                return (
                                    <a
                                        key={link.name}
                                        href={link.href}
                                        onClick={(e) => scrollToSection(e, link.href)}
                                        className={cn(
                                            "relative px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-300",
                                            isActive 
                                                ? "text-white bg-slate-800 shadow-inner" 
                                                : "text-slate-400 hover:text-white hover:bg-slate-800/50",
                                            link.isFeature && !isActive && "text-indigo-300 hover:text-indigo-200"
                                        )}
                                    >
                                        {link.isFeature && (
                                            <Sparkles className="w-3 h-3 inline-block mr-1.5 -mt-0.5 animate-pulse" />
                                        )}
                                        {link.name}
                                    </a>
                                );
                            })}
                        </div>

                        {/* Social Actions */}
                        <div className="flex items-center gap-3 pl-4 border-l border-slate-800">
                            <a
                                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group relative p-2 rounded-full bg-slate-800 hover:bg-[#25D366] transition-colors duration-300 overflow-hidden"
                                title="WhatsApp"
                            >
                                <WhatsAppLogo className="w-5 h-5 text-slate-300 group-hover:text-white relative z-10 transition-colors" />
                            </a>

                            <a
                                href={TEAMS_LINK}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group relative p-2 rounded-full bg-slate-800 hover:bg-[#464EB8] transition-colors duration-300"
                                title="Microsoft Teams"
                            >
                                <TeamsLogo className="w-5 h-5 text-slate-300 group-hover:text-white transition-colors" />
                            </a>
                        </div>
                    </div>

                    {/* Mobile Menu Button */}
                    <div className="lg:hidden flex items-center">
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors focus:outline-none"
                            aria-label="Toggle menu"
                        >
                            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        // Ensure the mobile menu is not scrollable itself, letting the main page scroll
                        className="lg:hidden bg-slate-950/95 backdrop-blur-xl border-b border-slate-800"
                    >
                        <div className="px-4 pt-4 pb-8 space-y-2">
                            {navLinks.map((link) => (
                                <a
                                    key={link.name}
                                    href={link.href}
                                    onClick={(e) => scrollToSection(e, link.href)}
                                    className={cn(
                                        "block px-4 py-3 rounded-xl text-base font-medium transition-all flex items-center justify-between group",
                                        activeSection === link.href.substring(1)
                                            ? "bg-indigo-600/10 text-indigo-300 border border-indigo-500/20"
                                            : "text-slate-400 hover:bg-slate-900 hover:text-white"
                                    )}
                                >
                                    <span className="flex items-center gap-3">
                                        {link.isFeature && <Sparkles className="w-4 h-4 text-indigo-400" />}
                                        {link.name}
                                    </span>
                                    <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all text-slate-500" />
                                </a>
                            ))}

                            <div className="mt-6 pt-6 border-t border-slate-800 grid grid-cols-2 gap-4">
                                <a
                                    href={`https://wa.me/${WHATSAPP_NUMBER}`}
                                    target="_blank"
                                    className="flex items-center justify-center gap-2 p-3 rounded-xl bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20 transition-colors border border-[#25D366]/20"
                                >
                                    <WhatsAppLogo className="w-5 h-5" />
                                    <span className="font-medium text-sm">WhatsApp</span>
                                </a>
                                <a
                                    href={TEAMS_LINK}
                                    target="_blank"
                                    className="flex items-center justify-center gap-2 p-3 rounded-xl bg-[#464EB8]/10 text-[#464EB8] hover:bg-[#464EB8]/20 transition-colors border border-[#464EB8]/20"
                                >
                                    <TeamsLogo className="w-5 h-5" />
                                    <span className="font-medium text-sm">Teams</span>
                                </a>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
}